
import type { EmoteRequest } from "../net/messages";
import { elem, installChrome, styleInstaller } from "./chrome";

export interface EmoteActions {
  /** Make a gesture. `targetId` is zero for one aimed at nobody. */
  onEmote?: (emote: number, targetId: number) => void;
  /** Allow or refuse, naming the token of the request on screen. */
  onAnswer?: (token: number, accept: boolean) => void;
}

export const EMOTE_CLAP = 1;
export const EMOTE_KISS = 51;
export const EMOTE_FRENCH_KISS = 52;
export const EMOTE_SLAP = 53;

/** The legacy bar's keyboard/chat command shown when a player hovers a gesture. */
const EMOTE_COMMANDS: Readonly<Record<number, string>> = {
  1: "/clap", 2: "/congratulation", 3: "/forgive", 4: "/angry",
  5: "/attractive", 6: "/sad", 7: "/shy", 8: "/cheerup",
  9: "/banter", 10: "/joy", 11: "/cheer1", 12: "/cheer2",
  13: "/dance1", 14: "/dance2", 15: "/dance3", 16: "/dance4",
  17: "/dance5", 18: "/dance6", 51: "/kiss", 52: "/french_kiss", 53: "/slap",
};

/** `ICON_DICT` (`emotion.py:55-82`) as `tools/build_ui_textures.py` imports it:
 *  the `.sub` and `.tga` names keep their path and become PNGs. */
export const EMOTE_ICONS: Readonly<Record<number, string>> = {
  1: "/ui/textures/game/windows/emotion_clap.png", // emotion.py:56
  2: "/ui/textures/icon/action/congratulation.png", // emotion.py:63
  3: "/ui/textures/icon/action/forgive.png", // emotion.py:64
  4: "/ui/textures/icon/action/angry.png", // emotion.py:65
  5: "/ui/textures/icon/action/attractive.png", // emotion.py:66
  6: "/ui/textures/icon/action/sad.png", // emotion.py:67
  7: "/ui/textures/icon/action/shy.png", // emotion.py:68
  8: "/ui/textures/icon/action/cheerup.png", // emotion.py:69
  9: "/ui/textures/icon/action/banter.png", // emotion.py:70
  10: "/ui/textures/icon/action/joy.png", // emotion.py:71
  11: "/ui/textures/game/windows/emotion_cheers_1.png", // emotion.py:57
  12: "/ui/textures/game/windows/emotion_cheers_2.png", // emotion.py:58
  13: "/ui/textures/icon/action/dance1.png", // emotion.py:72
  14: "/ui/textures/icon/action/dance2.png", // emotion.py:73
  15: "/ui/textures/icon/action/dance3.png", // emotion.py:74
  16: "/ui/textures/icon/action/dance4.png", // emotion.py:75
  17: "/ui/textures/icon/action/dance5.png", // emotion.py:76
  18: "/ui/textures/icon/action/dance6.png", // emotion.py:77
  51: "/ui/textures/game/windows/emotion_kiss.png", // emotion.py:79
  52: "/ui/textures/game/windows/emotion_french_kiss.png", // emotion.py:80
  53: "/ui/textures/game/windows/emotion_slap.png", // emotion.py:81
};

/** One button on the bar. */
export interface EmoteEntry {
  readonly id: number;
  /** What the button says. */
  readonly label: string;
  /** Whether this one needs somebody to aim at -- `NEED_PC`
   *  (`cmd_emotion.cpp:11`), true for exactly the three partnered gestures. */
  readonly partnered: boolean;
  readonly at?: string;
  /** Chat stem when it is not `label` (`locale_game.txt` vs the old verb). */
  readonly chat?: string;
}

export const EMOTES: readonly EmoteEntry[] = [
  // Labels are `locale_game.txt` `EMOTION_*` (`emotion.py:31-53`).
  { id: 1, label: "Claps", chat: "clap", partnered: false },
  { id: 2, label: "Approval", partnered: false },
  { id: 3, label: "Forgiveness", partnered: false },
  { id: 4, label: "Aggravating", partnered: false },
  { id: 5, label: "Seduction", partnered: false },
  { id: 6, label: "Sad", partnered: false },
  { id: 7, label: "Rejection", partnered: false },
  { id: 8, label: "Cheer", partnered: false },
  { id: 9, label: "Sneering", partnered: false },
  { id: 10, label: "Joy", partnered: false },
  { id: 11, label: "Cheers 1", partnered: false },
  { id: 12, label: "Cheers 2", partnered: false },
  { id: 13, label: "Dance 1", partnered: false },
  { id: 14, label: "Dance 2", partnered: false },
  { id: 15, label: "Dance 3", partnered: false },
  { id: 16, label: "Dance 4", partnered: false },
  { id: 17, label: "Dance 5", partnered: false },
  { id: 18, label: "Gumsan Style", partnered: false },
  { id: EMOTE_KISS, label: "Kiss", partnered: true, at: "kisses" },
  { id: EMOTE_FRENCH_KISS, label: "French Kiss", partnered: true, at: "french-kisses" },
  { id: EMOTE_SLAP, label: "Hit", partnered: true, at: "slaps" },
];

export function describe(
  emote: number,
  actorName: string,
  targetName: string,
): string {
  const entry = EMOTES.find((e) => e.id === emote);
  const who = actorName || "Somebody";
  if (entry === undefined) return `${who} makes a gesture.`;
  if (entry.at !== undefined && targetName) {
    return `${who} ${entry.at} ${targetName}.`;
  }
  return `${who}: ${(entry.chat ?? entry.label).toLowerCase()}.`;
}

const STYLE_ID = "emote-style";

const STYLE = `
.emotes {
  position: fixed;
  right: 12px;
  bottom: 120px;
  z-index: 58;
  width: 188px;
  padding: 8px 10px 10px;
  border-radius: 10px;
  background: rgba(12, 14, 18, 0.86);
  border: 1px solid rgba(170, 200, 255, 0.2);
  pointer-events: auto;
  font: 12px/1.4 system-ui, sans-serif;
  color: #d8dee6;
}
.emotes[hidden] { display: none; }
/* CharacterWindow Emotion page: fill the tab, no floating chrome
   (characterwindow.py:671-725). The ask prompt stays a separate modal. */
.charwin-page .emotes {
  position: absolute;
  inset: 0;
  right: auto;
  bottom: auto;
  z-index: auto;
  width: auto;
  padding: 8px 10px;
  border-radius: 0;
  background: transparent;
  border: 0;
  overflow: auto;
}
.charwin-page .emotes-title .emotes-target { max-width: 120px; }
.charwin-page .emotes-grid {
  grid-template-columns: repeat(6, 1fr);
  max-height: none;
}
.emotes-title {
  font-size: 0.66rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8fa0b4;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}
.emotes-target { color: #9fb6d4; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.emotes-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  max-height: 210px;
  overflow-y: auto;
}
.emote-button {
  position: relative;
  padding: 4px 2px;
  border-radius: 6px;
  border: 1px solid rgba(150, 200, 255, 0.28);
  background: rgba(150, 200, 255, 0.08);
  color: #cfe2ff;
  font: inherit;
  font-size: 11px;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.emote-button:hover:not(:disabled) { background: rgba(150, 200, 255, 0.2); }
.emote-icon { display: block; width: 32px; height: 32px; margin: 0 auto 2px; image-rendering: pixelated; }
.emote-button:disabled { opacity: 0.35; cursor: default; }
.emote-button-partnered { border-color: rgba(255, 170, 200, 0.34); color: #ffd2e2; }

.emote-ask {
  position: fixed;
  left: 50%;
  top: 36%;
  transform: translateX(-50%);
  z-index: 63;
  padding: 12px 16px;
  border-radius: 10px;
  background: rgba(12, 14, 18, 0.95);
  border: 1px solid rgba(255, 170, 200, 0.3);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  pointer-events: auto;
  font: 13px/1.5 system-ui, sans-serif;
  color: #d8dee6;
  text-align: center;
}
.emote-ask[hidden] { display: none; }
.emote-ask-text { margin-bottom: 4px; }
.emote-ask-note { color: #8fa0b4; font-size: 11px; margin-bottom: 8px; }
.emote-ask-buttons { display: flex; gap: 8px; }
.emote-ask-button {
  flex: 1;
  padding: 4px 12px;
  border-radius: 6px;
  border: 1px solid rgba(150, 200, 255, 0.34);
  background: rgba(150, 200, 255, 0.1);
  color: #cfe2ff;
  font: inherit;
  cursor: pointer;
}
.emote-ask-button:hover:not(:disabled) { background: rgba(150, 200, 255, 0.2); }
.emote-ask-button:disabled { opacity: 0.4; cursor: default; }
`;

const installStyle = styleInstaller(STYLE_ID, STYLE, installChrome);

export class EmotePanel {
  private readonly root: HTMLElement;
  private readonly targetLabel: HTMLElement;
  private readonly buttons: Map<number, HTMLButtonElement> = new Map();

  private readonly ask: HTMLElement;
  private readonly askText: HTMLElement;
  private readonly allow: HTMLButtonElement;
  private readonly refuse: HTMLButtonElement;

  /** Who is targeted, or zero. Only ever used to decide whether the three
   *  partnered buttons are pressable and what id goes on the wire -- never to
   *  decide whether a gesture is *allowed*, which is the server's. */
  private targetId = 0;

  private drawn: EmoteRequest | null = null;

  /** The token this panel has already answered, so a second click on one prompt
   *  cannot become a second answer. -1 when it has answered nothing. */
  private answered = -1;

  constructor(
    container: HTMLElement,
    private readonly actions: EmoteActions = {},
  ) {
    const doc = container.ownerDocument;
    installStyle(doc);

    this.root = elem(doc, "div", "emotes");
    this.root.hidden = true;

    const title = elem(doc, "div", "emotes-title");
    const label = doc.createElement("span");
    // `CHARACTER_ACTION`/`CHARACTER_EMOTICON` (`locale_interface.txt:9,10`),
    // not the house word "Gestures".
    label.textContent = "Emotion";
    this.targetLabel = elem(doc, "span", "emotes-target");
    title.append(label, this.targetLabel);

    const grid = elem(doc, "div", "emotes-grid");
    for (const entry of EMOTES) {
      const button = doc.createElement("button");
      button.type = "button";
      button.className = "emote-button";
      if (entry.partnered) button.classList.add("emote-button-partnered");
      const icon = doc.createElement("img");
      icon.className = "emote-icon";
      icon.alt = "";
      const iconPath = EMOTE_ICONS[entry.id];
      if (iconPath !== undefined) icon.src = iconPath;
      button.append(icon, doc.createTextNode(entry.label));
      button.title = entry.partnered
        ? `${entry.label} -- needs a target, and their permission`
        : entry.label;
      const command = EMOTE_COMMANDS[entry.id];
      if (command !== undefined) button.title += ` (command: ${command})`;
      button.addEventListener("click", () => this.press(entry));
      // `SetCoverButton(slotIdx)` over every emoticon slot, in the four faces
      // its defaults name (`uicharacter.py:309`, `root/ui.py:1269-1272`).
      button.append(elem(doc, "span", "m2-slot-cover"));
      grid.append(button);
      this.buttons.set(entry.id, button);
    }

    this.root.append(title, grid);

    this.ask = elem(doc, "div", "emote-ask");
    this.ask.hidden = true;
    this.askText = elem(doc, "div", "emote-ask-text");
    const note = elem(doc, "div", "emote-ask-note");
    // Said out loud because the server's own answer to Allow is silence: it
    // records permission and plays nothing. A player who pressed it, saw
    // nothing, and was not told this would press it again.
    note.textContent = "Allowing does not play it -- they have to try again.";
    const buttons = elem(doc, "div", "emote-ask-buttons");
    this.allow = doc.createElement("button");
    this.allow.type = "button";
    this.allow.className = "emote-ask-button";
    this.allow.textContent = "Allow";
    this.allow.addEventListener("click", () => this.answer(true));
    this.refuse = doc.createElement("button");
    this.refuse.type = "button";
    this.refuse.className = "emote-ask-button";
    this.refuse.textContent = "No";
    this.refuse.addEventListener("click", () => this.answer(false));
    buttons.append(this.allow, this.refuse);
    this.ask.append(this.askText, note, buttons);

    container.append(this.root, this.ask);
    this.retarget(0, "");
  }

  get showing(): boolean {
    return !this.root.hidden;
  }

  get element(): HTMLElement {
    return this.root;
  }

  get asking(): boolean {
    return !this.ask.hidden;
  }

  /** The token Allow would send. -1 when no prompt is drawn. For tests. */
  get shownToken(): number {
    return this.drawn === null ? -1 : this.drawn.token;
  }

  /** Show or hide the bar. */
  setVisible(visible: boolean): void {
    this.root.hidden = !visible;
  }

  retarget(entityId: number, name: string): void {
    this.targetId = entityId;
    this.targetLabel.textContent = entityId === 0 ? "" : name || "target";
    for (const entry of EMOTES) {
      const button = this.buttons.get(entry.id);
      if (button === undefined) continue;
      button.disabled = entry.partnered && entityId === 0;
      button.querySelector(".m2-slot-cover")
        ?.setAttribute("aria-disabled", String(button.disabled));
    }
  }

  requested(request: EmoteRequest): void {
    if (request.token === 0) {
      this.drawn = null;
      this.ask.hidden = true;
      return;
    }
    this.drawn = request;
    const who = request.actor_name || "Somebody";
    const entry = EMOTES.find((e) => e.id === request.emote);
    const what = entry === undefined ? "make a gesture at" : entry.label.toLowerCase();
    this.askText.textContent = `${who} wants to ${what} you.`;
    this.setAnswerable(true);
    this.ask.hidden = false;
  }

  private press(entry: EmoteEntry): void {
    if (entry.partnered && this.targetId === 0) return;
    this.actions.onEmote?.(entry.id, entry.partnered ? this.targetId : 0);
  }

  private answer(accept: boolean): void {
    const drawn = this.drawn;
    // The token of what is on screen. Never the connection's current request:
    // if a second one has arrived since this was drawn, the server refuses this
    // token, and refusing is the correct outcome -- the player agreed to what
    // they read, and what they read was a name.
    if (drawn === null || drawn.token === this.answered) return;
    this.answered = drawn.token;
    this.setAnswerable(false);
    this.actions.onAnswer?.(drawn.token, accept);
  }

  private setAnswerable(can: boolean): void {
    this.allow.disabled = !can;
    this.refuse.disabled = !can;
  }
}
